class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.resultsBox = this.querySelector('.search__box.search__results');

    this._pointerDownInside = false;
    this.addEventListener('pointerdown', (e) => {
      if (this.contains(e.target)) {
        this._pointerDownInside = true;
        setTimeout(() => (this._pointerDownInside = false), 150);
      }
    }, true);

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
      // Close when focus leaves the predictive search UI (helps mobile/touch)
      this.addEventListener('focusout', this.handleFocusOut.bind(this), true);
      // Handle close button click/double-tap
      const closeButton = this.querySelector('.search__close-button');
      if (closeButton) {
        // Show close button only on Android devices
        if (this.isAndroid) {
          closeButton.classList.remove('hidden');
        }
        closeButton.addEventListener('click', this.handleCloseButtonClick.bind(this));
      }
      
      // Watch for class changes on body to handle aria-hidden (works with inline scripts too)
      if (this.isAndroid) {
        this.observeBodyClassChanges();
      }
    }
  }

  observeBodyClassChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasClass = document.body.classList.contains('predictive-search-open');
          const hadClass = mutation.oldValue && mutation.oldValue.includes('predictive-search-open');
          
          // If class was removed, remove aria-hidden
          if (hadClass && !hasClass) {
            this.setAriaHiddenForMainElements(false);
          }
          if (this.input) {
            this.input.setAttribute('aria-expanded', hasClass ? 'true' : 'false');
          }
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class']
    });
    
    // Store observer so it doesn't get garbage collected
    this._bodyClassObserver = observer;
  }

  get isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      // Android: Add aria-hidden to main elements except template-search__header
      if (this.isAndroid) {
        this.setAriaHiddenForMainElements(true);
      }
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    // Announce that search has been cleared
    FoxTheme.a11y.announceToScreenReader('Search cleared.');
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');
    
    // Announce that search is loading
    FoxTheme.a11y.announceToScreenReader('Searching...');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.input.setAttribute('aria-expanded', 'true');
          // Find a listbox and the first option
          const listbox = targetElement.querySelector('[role="listbox"]');
          const firstOption = targetElement.querySelector('[role="option"]');
          if (listbox) {
            // Ensure listbox can be focused
            listbox.setAttribute('tabindex', '-1');
          }
          if (firstOption && firstOption.id) {
            // Announce an active option from the combobox
            this.input.setAttribute('aria-activedescendant', firstOption.id);
            firstOption.setAttribute('aria-selected', 'true'); // helps VO announce
          }

          // iOS VO handoff: briefly focus the listbox so VO discovers results,
          // then hand focus back to the input to keep combobox semantics
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
          if (isIOS && listbox) {
            setTimeout(() => {
              try { listbox.focus({ preventScroll: true }); } catch(e) {}
              setTimeout(() => {
                try { this.input.focus({ preventScroll: true }); } catch(e) {}
              }, 30);
            }, 30);
          }

this.announceSearchResults(contentElement);
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
        // Announce error to screen readers
        FoxTheme.a11y.announceToScreenReader('Search failed. Please try again.');
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
      // Announce error to screen readers
      FoxTheme.a11y.announceToScreenReader('Search failed. Please try again.');
    }
  }

  announceSearchResults(contentElement) {
    // Count different types of results
    const products = contentElement.querySelectorAll('[id^="predictive-search-option-product-"]');
    const collections = contentElement.querySelectorAll('[id^="predictive-search-option-collection-"]');
    const articles = contentElement.querySelectorAll('[id^="predictive-search-option-article-"], [id^="predictive-search-option-page-"]');
    const queries = contentElement.querySelectorAll('[id^="predictive-search-option-query-"]');
    
    const totalResults = products.length;
    
    if (totalResults === 0) {
      FoxTheme.a11y.announceToScreenReader('No results found for your search.');
    } else {
      let announcement = `Found ${totalResults} result${totalResults === 1 ? '' : 's'}`;
      
      // Add specific counts for each type if they exist
      const resultTypes = [];
      if (products.length > 0) resultTypes.push(`${products.length} product${products.length === 1 ? '' : 's'}`);

      if (resultTypes.length > 0) {
        announcement += `: ${resultTypes.join(', ')}`;
      }
      
      announcement += '. Use tab key to navigate results.';
      FoxTheme.a11y.announceToScreenReader(announcement);
    }
  }

  setAriaHiddenForMainElements(hide) {
    // Find all main elements except .template-search__header
    const mainElements = document.querySelectorAll('main, header, footer, [role="main"], [role="banner"], [role="contentinfo"], #sr-announce, .collection__content');
    const templateSearchHeader = document.querySelector('.template-search__header');
    
    // Helper function to set/remove aria-hidden
    const setAriaHidden = (element) => {
      if (!element) return;
      
      if (hide) {
        if (!element.dataset.originalAriaHidden) {
          element.dataset.originalAriaHidden = element.getAttribute('aria-hidden') || '';
        }
        element.setAttribute('aria-hidden', 'true');
      } else {
        const original = element.dataset.originalAriaHidden;
        if (original === '') {
          element.removeAttribute('aria-hidden');
        } else if (original) {
          element.setAttribute('aria-hidden', original);
        } else {
          element.removeAttribute('aria-hidden');
        }
        delete element.dataset.originalAriaHidden;
      }
    };
    
    // Handle main elements (header, footer, main, etc.)
    mainElements.forEach((element) => {
      // Skip if it's the template-search__header or contains it
      if (templateSearchHeader) {
        // If this element IS the template-search__header, skip it
        if (element === templateSearchHeader || element.classList.contains('template-search__header')) {
          return;
        }
        // If this element contains template-search__header, we need to hide its children except the header
        if (element.contains(templateSearchHeader)) {
          // Hide all direct children except template-search__header
          Array.from(element.children).forEach((child) => {
            if (child !== templateSearchHeader && !child.contains(templateSearchHeader)) {
              setAriaHidden(child);
            }
          });
          return; // Don't hide the main element itself if it contains the header
        }
      }
      
      setAriaHidden(element);
    });
  }

  closeSearchPopup() {
    document.body.classList.remove('predictive-search-open');
    // Android: Remove aria-hidden from main elements
    if (this.isAndroid) {
      this.setAriaHiddenForMainElements(false);
    }
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
  }

  handleCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeSearchPopup();
    
    // Focus on facet-drawer-button after closing
    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        const facetButton = document.querySelector('.facet-drawer-button');
        if (facetButton) {
          try {
            // Ensure button is focusable
            if (facetButton.hasAttribute('tabindex') && facetButton.getAttribute('tabindex') === '-1') {
              facetButton.removeAttribute('tabindex');
            }
            facetButton.focus();
          } catch (e) {
            // Fallback to input if facet button not available
            this.input.focus();
          }
        } else {
          // Fallback to input if facet button not found
          this.input.focus();
        }
      }, 50);
    });
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        this.closeSearchPopup();
      });
    }
  }

  handleFocusOut(event) {
    if (!document.body.classList.contains('predictive-search-open')) return;

    // If a tap started inside, don't close (Android often blurs before touch)
    if (this._pointerDownInside) return;

    const nextFocused = event.relatedTarget;

    // If browser didn't report where focus goes (Android), don't close here.
    if (!nextFocused) return;

    // If focus stays within the predictive search component, keep open
    if (this.contains(nextFocused)) return;

    setTimeout(() => {
      const active = document.activeElement;
      if (active && this.contains(active)) return;

      this.closeSearchPopup();
    }, 0);
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
